import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, ScanFace, Gavel, Trophy, Wallet, Bot, ExternalLink, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, money, shortDate } from "../lib/api";

const Tile = ({ icon: Icon, k, v, testid, delay }) => (
  <div className="relative overflow-hidden border border-white/10 bg-white/[0.03] p-4 rise" style={{ animationDelay: `${delay}ms` }} data-testid={testid}>
    <div className="absolute -right-6 -top-6 w-20 h-20 rounded-full blur-2xl" style={{ background: "rgb(var(--brand-rgb)/0.18)" }} />
    <Icon className="w-4 h-4 text-brand" />
    <p className="font-mono text-2xl font-extrabold mt-2 tabular">{v}</p>
    <p className="text-[11px] text-white/50 mt-0.5">{k}</p>
  </div>
);

const Body = ({ s, ws }) => {
  const wid = ws?.id || s.workspace_id;
  const link = `${window.location.origin}/verify${wid && wid !== "ws_default" ? `?w=${wid}` : ""}`;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Tile icon={ScanFace} k="Customers verified" v={s.verified} testid="wa-verified" delay={0} />
        <Tile icon={Gavel} k="Disputes defended" v={s.defended} testid="wa-defended" delay={60} />
        <Tile icon={Trophy} k="Win rate" v={s.win_rate == null ? "—" : `${s.win_rate}%`} testid="wa-winrate" delay={120} />
        <Tile icon={Wallet} k="Recovered" v={money(s.recovered)} testid="wa-recovered" delay={180} />
        <Tile icon={Bot} k="Autopilot actions" v={s.autopilot.length} testid="wa-autopilot-count" delay={240} />
      </div>
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="border border-white/10 p-4" data-testid="wa-setup">
          <p className="label-xs">Setup</p>
          <p className="text-sm text-white/70 mt-2">Add this as your post-upsell page. Customers verify in about 10 seconds:</p>
          <button onClick={() => navigator.clipboard.writeText(link).then(() => toast.success("Link copied"))} className="mt-2 w-full flex items-center gap-2 bg-black/40 border border-white/10 px-3 py-2 font-mono text-[11px] text-brand text-left break-all" data-testid="wa-verify-link"><Copy className="w-3.5 h-3.5 shrink-0" />{link}</button>
          <p className="text-xs text-white/45 mt-3">{s.whop_connected ? "Whop events are flowing in." : "Waiting for your first Whop event."} Autopilot drafts, submits or refunds automatically and logs every decision.</p>
        </div>
        <div className="border border-white/10 p-4" data-testid="wa-autopilot">
          <p className="label-xs">Autopilot decisions</p>
          <ul className="mt-2 divide-y divide-white/5 text-sm">
            {s.autopilot.map((a) => (
              <li key={a.id} className="py-2 flex gap-3"><span className={`font-mono text-[10px] w-16 ${a.decision === "DEFEND" ? "text-brand" : "text-amber-300"}`}>{a.decision}</span><span className="flex-1 text-white/70 truncate">{a.detail}</span><span className="font-mono text-[10px] text-white/40">{a.confidence}%</span></li>
            ))}
            {!s.autopilot.length && <li className="py-2 text-white/45">No disputes yet.</li>}
          </ul>
        </div>
      </div>
      <div className="border border-white/10 p-4" data-testid="wa-open-disputes">
        <p className="label-xs">Open disputes</p>
        <ul className="mt-2 divide-y divide-white/5 text-sm">
          {s.open.map((d) => <li key={d.id} className="py-2 flex gap-3"><span className="flex-1">{d.customer_name}</span><span className="font-mono">{money(d.amount)}</span><span className="text-white/45 text-xs">{shortDate(d.created_at)}</span></li>)}
          {!s.open.length && <li className="py-2 text-white/45">Nothing needs you right now.</li>}
        </ul>
      </div>
    </div>
  );
};

const DEV_TOKEN = new URLSearchParams(window.location.hash.slice(1)).get("dev_token");

export default function WhopApp() {
  const { companyId } = useParams();
  const [ws, setWs] = useState(null);
  const [s, setS] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    const dev = DEV_TOKEN;
    if (window.location.hash) window.history.replaceState(null, "", window.location.pathname);
    const start = dev
      ? Promise.resolve(sessionStorage.setItem("aegis-embed-token", dev))
      : api.post("/whop-app/session", { company_id: companyId }).then((r) => { sessionStorage.setItem("aegis-embed-token", r.data.token); setWs(r.data.workspace); });
    start.then(() => api.get("/whop-app/stats")).then((r) => setS(r.data)).catch((e) => setErr(e.response?.data?.detail || "Couldn't open AEGIS."));
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="min-h-screen bg-[#07090c] text-white p-5 sm:p-7" data-testid="whop-app">
      <header className="flex items-center justify-between mb-6">
        <p className="flex items-center gap-2 font-bold"><ShieldCheck className="w-5 h-5 text-brand" /> AEGIS Chargeback Protection <span className="font-mono text-[10px] text-white/40 ml-2">{companyId}</span></p>
        <a href="/app" target="_blank" rel="noreferrer" className="flex items-center gap-1.5 font-mono text-[10px] text-brand" data-testid="wa-open-full">OPEN FULL AEGIS <ExternalLink className="w-3 h-3" /></a>
      </header>
      {err ? <p className="text-sm text-amber-300" data-testid="wa-error">{err}</p> : !s ? <p className="font-mono text-xs text-white/40">connecting to Whop…</p> : <Body s={s} ws={ws} />}
    </div>
  );
}
